// ============= Imports =============
import { AuthContextProps } from '../../../contexts/AuthContext';
import { FormData, FormDataFormatted } from '../../../types/formData.types';
import { tokenManager } from '@tw/tw-component-library';
import { createLogger } from '../../../utils/logger';

// ============= Types =============
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface DateFormatOptions {
  locale?: string | string[];
  options?: Intl.DateTimeFormatOptions;
}

export interface GetWorkFlow {
  getAccessToken: AuthContextProps['getAccessToken'];
  id: string;
  type: number;
  spoofedUser: string | null;
}

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Workflow';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';
const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

const HEADERS: HeadersInit = {
  'Content-Type': 'application/json',
};

// ============= Logger =============
const logger = createLogger('GetWorkFlow');

// ============= Utilities =============
const createHeaders = (token: string, spoofedUser: string | null) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
  ...(spoofedUser && {
    'x-Spoof-User': spoofedUser,
  }),
});

const formatDate = (
  dateString: string,
  options: DateFormatOptions = {}
): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(
    options.locale,
    options.options || DEFAULT_DATE_OPTIONS
  );
};

/**
 * Only formats the dates in the workflow data
 */
const formatWorkflowDates = (workflowData: FormData): FormDataFormatted => {
  return {
    ...workflowData,
    createdDate: formatDate(workflowData.createdDate),
    submittedDate: formatDate(workflowData.submittedDate),
  };
};

// ============= Main Function =============
export const getWorkFlow = async ({
  getAccessToken,
  id,
  type,
  spoofedUser,
}: GetWorkFlow): Promise<ApiResponse<FormDataFormatted[]>> => {
  try {
    logger.debug('Fetching workflow data', { id, type });

    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    const url = new URL(`${BASE_URL}${API_ENDPOINT}/${id}`);
    url.searchParams.append('type', type.toString());
    const encodedUrl = encodeURI(url.toString());

    logger.debug('Making request to API', { url: encodedUrl });

    const response = await fetch(encodedUrl, {
      method: 'GET',
      headers: createHeaders(token, spoofedUser || null),
    });

    if (!response.ok) {
      const errorMessage = `HTTP error! status: ${response.status}`;
      logger.error('HTTP error occurred', {
        status: response.status,
        errorMessage,
      });
      return {
        success: false,
        error: errorMessage,
      };
    }

    const rawData = (await response.json()) as FormData;

    const formattedData = formatWorkflowDates(rawData);

    logger.debug('Workflow data fetched and formatted successfully');

    return {
      success: true,
      data: [formattedData],
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'An unknown error occurred';

    logger.error('Error fetching workflow data', { error: errorMessage });

    return {
      success: false,
      error: errorMessage,
    };
  }
};
